import Warning from '@mui/icons-material/Warning';
import { Box, Link, Spacer, Stack, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import Icon from '~/components-ds/Icon';
import Modal from '~/components-ds/Modal';
import { UnorderedList, UnorderedListItem } from '~/components-ds/UnorderedList';
import { useAppContent, useAppDispatch } from '~/hooks';
import { qalTrackBusinessErrorAction } from '~/thunks/analytics';
import type { ErrorType } from '~/types/error';
import { isJsonString } from '~/utils/analytics/trackUtils';

interface DeclineModalProps {
  errors: ErrorType[];
}

const DeclineModal: React.FC<DeclineModalProps> = ({ errors }) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.declineModal',
  });

  const dispatch = useAppDispatch();
  useEffect(() => {
    const bulkActions = errors.map((error) => {
      const { message, code, question } = error;
      return {
        errorCode: code,
        message: isJsonString(message) ? JSON.parse(message) : message,
        question,
        errorType: 'business rule decline',
      };
    });

    dispatch(qalTrackBusinessErrorAction(bulkActions));
    // Limit this useEffect to fire only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      open
      id="decline-modal"
      title={getTextContent('title')}
      data-track="return to QBE"
      primaryBtnProps={{
        label: getSharedContent('returnToQbeButtonLabel'),
        onClick: () => {
          window.location.href = getSharedContent('qbeBusinessInsuranceHomeLink');
        },
      }}
      iconProps={{
        muiIcon: Warning,
        color: '#d43900',
      }}
    >
      <>
        <Box>
          <Typography>{getTextContent('description')}</Typography>
          <Spacer size="sm" />

          <UnorderedList>
            {errors.map((error) => (
              <UnorderedListItem sx={{ color: '#00205b' }} key={error.code || error.message}>
                {error.message}
              </UnorderedListItem>
            ))}
          </UnorderedList>

          <Typography>{getTextContent('fields.otherInsuranceOptions.title')}</Typography>
        </Box>
        <Spacer size="lg" />

        <Box fontSize="12px" textAlign="center">
          <Stack direction="row" spacing={2} width="100%" justifyContent="center">
            <Box>
              <span style={{ display: 'block', marginBottom: '10px' }}>
                {getTextContent('fields.otherInsuranceOptions.partner')}
              </span>
              <Link
                target="_blank"
                title={getTextContent('fields.elders.title')}
                href={getTextContent('fields.elders.link')}
                data-track="Go to Elders"
                aria-label={getTextContent('fields.elders.title')}
              >
                <Icon name="elders" width={98} height={56} />
                {/* TODO */}
              </Link>
            </Box>
            <Box>
              <span style={{ display: 'block' }}>
                {getTextContent('fields.otherInsuranceOptions.contacting')}
              </span>
              <Spacer size="xxs" />
              <Link
                target="_blank"
                title={getTextContent('fields.insuranceCouncil.title')}
                href={getTextContent('fields.insuranceCouncil.link')}
                data-track="Go to Insurance Council of Australia"
                aria-label={getTextContent('fields.insuranceCouncil.title')}
              >
                <Icon name="insuranceCouncil" width={167} height={56} />
              </Link>
            </Box>
          </Stack>
        </Box>
        <Spacer size="lg" />

        <Typography>{getJSXContent('content.footer.description')}</Typography>
      </>
    </Modal>
  );
};
export default DeclineModal;
